import { useRef, useState } from 'react';
import * as api from './api';

/**
 * Блок видео.
 *
 * Почему не голый `<video controls>`: вебвью Telegram не рисует первый кадр
 * при `preload="metadata"` — женщина видит чёрный прямоугольник и думает,
 * что ничего не загрузилось. Поэтому кадр показываем сами.
 *
 * И не грузим видео до нажатия: ролик весит десятки мегабайт, а на первом
 * экране она ещё не решила, будет ли смотреть. Двадцать мегабайт в фоне
 * на мобильной сети — это несколько секунд, за которые можно уйти.
 */
export function VideoBlock({
  url,
  caption,
  posterUrl,
  aspect,
  screen,
}: {
  url: string | null;
  caption: string | null;
  posterUrl?: string | null;
  /** Отношение ширины к высоте. Видео Елены вертикальное — 16:9 ему не подходит. */
  aspect?: number | null;
  /**
   * Экран, на котором стоит ролик.
   *
   * Нужен только отчёту: «видео посмотрели 40 раз» без экрана не отвечает
   * ни на один вопрос — приветственное это видео или практика третьего дня.
   */
  screen?: string;
}) {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Вертикальное видео в рамке 16:9 даёт широкую пустую коробку.
  // Без подсказки берём умеренно вертикальный кадр: так реже промахиваемся.
  const ratio = aspect && aspect > 0 ? aspect : 4 / 5;

  if (!url) {
    return (
      <div className="video-frame placeholder" style={{ aspectRatio: String(ratio) }}>
        <span className="play-icon" aria-hidden="true" />
        <span className="video-caption">{caption ?? 'Видео временно недоступно'}</span>
      </div>
    );
  }

  if (playing) {
    return (
      <video
        ref={videoRef}
        className="video-frame"
        style={{ aspectRatio: String(ratio) }}
        src={url}
        poster={posterUrl ?? undefined}
        controls
        autoPlay
        playsInline
        preload="auto"
        onEnded={() => api.trackEvent('video_completed', { screen: screen ?? '' })}
      >
        Ваш браузер не поддерживает видео.
      </video>
    );
  }

  return (
    <button
      type="button"
      className="video-frame poster"
      style={{
        aspectRatio: String(ratio),
        backgroundImage: posterUrl ? `url(${posterUrl})` : undefined,
      }}
      onClick={() => {
        api.trackEvent('video_started', { screen: screen ?? '' });
        setPlaying(true);
      }}
      aria-label={caption ? `Смотреть: ${caption}` : 'Смотреть видео'}
    >
      {/* Затемнение поверх кадра: иначе кнопка теряется на светлом лице. */}
      <span className="video-scrim" aria-hidden="true" />
      <span className="play-icon big" aria-hidden="true" />
      {caption ? <span className="video-caption on-poster">{caption}</span> : null}
    </button>
  );
}
